import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

import {
  ERROR_MESSAGES,
  type ErrorCode,
  type ProcessingJobDto,
} from "@wendo/contracts";

import { documentApi, type DocumentApi } from "../../services/api-client";
import { downloadFromUrl } from "../../services/download";
import {
  uploadFileToR2,
  validatePdfFile,
  type UploadFile,
} from "../../services/upload";

export type ConversionPhase =
  | "idle"
  | "validating"
  | "uploading"
  | "ready"
  | "creating"
  | "processing"
  | "success"
  | "error";

function terminal(job: ProcessingJobDto | undefined): boolean {
  return (
    job?.status === "SUCCEEDED" ||
    job?.status === "FAILED" ||
    job?.status === "CANCELED"
  );
}

function publicError(error: unknown): string {
  return error instanceof Error ? error.message : ERROR_MESSAGES.INTERNAL_ERROR;
}

export function useConversion(
  api: DocumentApi = documentApi,
  uploadFile: UploadFile = uploadFileToR2,
) {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [assetId, setAssetId] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [phase, setPhase] = useState<ConversionPhase>("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [localError, setLocalError] = useState<string | null>(null);
  const [downloadingAssetId, setDownloadingAssetId] = useState<string | null>(
    null,
  );

  const jobQuery = useQuery({
    queryKey: ["conversion-job", jobId],
    queryFn: () => api.getJob(jobId!),
    enabled: jobId !== null,
    refetchInterval: (query) =>
      terminal(query.state.data?.job) ? false : 2_000,
    retry: 1,
  });

  const job = jobQuery.data?.job;
  const visiblePhase: ConversionPhase = (() => {
    if (job?.status === "SUCCEEDED") return "success";
    if (job?.status === "FAILED" || job?.status === "CANCELED") return "error";
    if (jobId !== null && phase !== "creating") return "processing";
    if (jobQuery.error !== null) return "error";
    return phase;
  })();

  const errorMessage = (() => {
    if (localError !== null) return localError;
    if (jobQuery.error !== null) return publicError(jobQuery.error);
    if (job?.errorCode !== null && job?.errorCode !== undefined) {
      return (
        ERROR_MESSAGES[job.errorCode as ErrorCode] ??
        ERROR_MESSAGES.CONVERSION_FAILED
      );
    }
    return null;
  })();

  const selectFile = useCallback(
    async (file: File) => {
      setSelectedFile(file);
      setLocalError(null);
      setUploadProgress(0);
      setPhase("validating");
      try {
        await validatePdfFile(file);
        const upload = await api.createUploadSession({
          fileName: file.name,
          contentType: "application/pdf",
          size: file.size,
        });
        setAssetId(upload.assetId);
        setPhase("uploading");
        await uploadFile(upload.uploadUrl, file, setUploadProgress);
        await api.completeUpload(upload.assetId);
        setPhase("ready");
      } catch (error: unknown) {
        setLocalError(publicError(error));
        setPhase("error");
      }
    },
    [api, uploadFile],
  );

  const startConversion = useCallback(async () => {
    if (assetId === null) return;
    setLocalError(null);
    setPhase("creating");
    try {
      const created = await api.createJob(
        {
          capability: "pdf.to.docx",
          inputAssetIds: [assetId],
          options: {
            ocr: "auto",
            language: "auto",
            layoutMode: "preserve-layout",
          },
        },
        crypto.randomUUID(),
      );
      setJobId(created.jobId);
      setPhase("processing");
    } catch (error: unknown) {
      setLocalError(publicError(error));
      setPhase("error");
    }
  }, [api, assetId]);

  const removeFile = useCallback(async () => {
    if (assetId !== null) {
      try {
        await api.deleteAsset(assetId);
      } catch (error: unknown) {
        setLocalError(publicError(error));
        setPhase("error");
        return;
      }
    }
    setSelectedFile(null);
    setAssetId(null);
    setUploadProgress(0);
    setLocalError(null);
    setPhase("idle");
  }, [api, assetId]);

  const reset = useCallback(async () => {
    try {
      await api.cleanup();
    } finally {
      await queryClient.cancelQueries({ queryKey: ["conversion-job"] });
      queryClient.removeQueries({ queryKey: ["conversion-job"] });
      setSelectedFile(null);
      setAssetId(null);
      setJobId(null);
      setUploadProgress(0);
      setLocalError(null);
      setDownloadingAssetId(null);
      setPhase("idle");
    }
  }, [api, queryClient]);

  const download = useCallback(
    async (targetAssetId: string) => {
      setDownloadingAssetId(targetAssetId);
      setLocalError(null);
      try {
        const session = await api.createDownloadSession(targetAssetId);
        await downloadFromUrl(session.downloadUrl, session.fileName);
      } catch (error: unknown) {
        setLocalError(publicError(error));
      } finally {
        setDownloadingAssetId(null);
      }
    },
    [api],
  );

  useEffect(() => {
    const onPageHide = () => api.cleanupKeepalive();
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [api]);

  useEffect(() => {
    if (assetId === null) return undefined;
    const interval = window.setInterval(() => {
      void api.heartbeat().catch(() => {
        // Polling and the server-side TTL remain authoritative if a heartbeat is interrupted.
      });
    }, 60_000);
    return () => window.clearInterval(interval);
  }, [api, assetId]);

  return {
    phase: visiblePhase,
    selectedFile,
    assetId,
    uploadProgress,
    job,
    progress:
      job?.progress ?? (visiblePhase === "uploading" ? uploadProgress : 0),
    errorMessage,
    downloadingAssetId,
    selectFile,
    startConversion,
    removeFile,
    reset,
    download,
  };
}
